import { Col, Form, Row, Typography } from 'antd'
import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'

import { InputField } from 'src/components/form'
import {
  InputLabel,
  LabelRequire,
} from 'src/components/layout/Header/HeaderStyle'
import { UserRequestInterface } from 'src/interfaces/user'
import { UseFormReturn } from 'react-hook-form'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import ButtonCustom from 'src/components/form/Button'
import addressRequest from 'src/api/requests/address'
import { PATH as AUTOMATIC_PLAN_PATH } from 'src/pages/automaticPlan'
import { PATH as PATH_MY_PAGE } from 'src/pages/myPage'
import { getMenuById } from 'src/useCase/question'
import { getInfo, updateUserInfo } from 'src/useCase/user'
import { createUserTreatment } from 'src/useCase/userTreatment'
import { useAppDispatch, useAppSelector } from 'src/store'
import { getClinicDetail } from 'src/useCase/clinic'
import DuplicateAccountModal from 'src/components/Modal/DuplicateAccountModal'
import GenderSelection from 'src/components/form/GenderSelection'
import {
  getStoredSelectedGender,
  hasEffectiveGender,
  isMenuGenderIncompatible,
  resolveEffectiveGender,
  setStoredSelectedGender,
  shouldOfferGenderSelection,
} from 'src/utils/gender'

type Props = {
  flowName?: string | null
  form: UseFormReturn<UserRequestInterface>
  onSubmitRegister: (
    data: UserRequestInterface,
    redirectPath: string,
    currentFlow: string | null,
  ) => Promise<boolean>
  asForm?: boolean
  disabled?: boolean
}

const UserInfoForm = ({
  flowName,
  form,
  onSubmitRegister,
  asForm = true,
  disabled = false,
}: Props) => {
  const navigate = useNavigate()
  const paramsUrl = useParams()
  const [searchParams] = useSearchParams()
  const menuId = searchParams.get('menuId') || paramsUrl?.id
  const clinicId = searchParams.get('clinicId')
  const planId = searchParams.get('planId')
  const dispatch = useAppDispatch()
  const userInfo = useAppSelector((state) => state.ui.app.authInfo)
  const [menuTargetGender, setMenuTargetGender] = useState<string>()
  const [userSelectedGender, setUserSelectedGender] = useState<string>()
  const [genderError, setGenderError] = useState(false)
  const [isAgreePolicy, setIsAgreePolicy] = useState(false)
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false)
  const [isEmailVerified, setIsEmailVerified] = useState(false)
  const uuid = searchParams.get('uuid')
  const genderFromUrl =
    searchParams.get('gender') || searchParams.get('gender_id')
  const storedGender = getStoredSelectedGender()

  const genderSources = useMemo(
    () => ({
      userGenderId: userInfo?.gender_id,
      genderFromUrl,
      selectedGenderFromStorage: storedGender,
      localSelection: userSelectedGender,
    }),
    [userInfo?.gender_id, genderFromUrl, storedGender, userSelectedGender],
  )

  // Intentional (DB_219): require gender for every flow without a fixed profile/URL
  // source — not limited to LP + planId. See docs/gender-selection-spec.md.
  const showGenderSelection = shouldOfferGenderSelection(genderSources)
  const canProceedWithGender = hasEffectiveGender(genderSources)
  const genderDisplayValue =
    userSelectedGender || genderFromUrl || storedGender || undefined

  const Component = asForm ? Form : 'div'

  const {
    control,
    formState: { isSubmitting },
    handleSubmit,
    setFocus,
  } = form

  const handleBlur = async (value: string) => {
    if (!value) {
      setIsEmailVerified(false)
      return
    }

    if (form.formState.errors.email) return

    try {
      await addressRequest.verifyUser({ email: value })
      form.clearErrors('email')
      setIsEmailVerified(true)
    } catch (err: any) {
      const apiMessage =
        err?.response?.data?.errors?.email?.[0] || err?.response?.data?.message

      const isDuplicateError = apiMessage === 'このメールは既に使われています'

      if (isDuplicateError) {
        setIsDuplicateModalOpen(true)
      }

      form.setError('email', {
        type: 'manual',
        message: apiMessage,
      })
      setIsEmailVerified(false)
    }
  }

  useEffect(() => {
    if (!uuid) return
    ;(async () => {
      try {
        await dispatch(getClinicDetail({ uuid })).unwrap()
      } catch {
        // Best-effort — clinic fetch failure does not block registration
      }
    })()
  }, [uuid, dispatch])

  useEffect(() => {
    const prefilled = genderFromUrl || storedGender
    if (prefilled && !userSelectedGender) {
      setUserSelectedGender(prefilled)
      form.setValue('gender_id', prefilled)
    }
  }, [genderFromUrl, storedGender, userSelectedGender, form])

  useEffect(() => {
    if (menuId && clinicId) {
      dispatch(
        getMenuById({
          menuId,
          params: {
            clinic_id: clinicId,
            select_field: 'target_gender',
          },
        }),
      )
        .then((res: any) => {
          const gender = res.payload.target_gender
          setMenuTargetGender(`${gender}`)
        })
        .catch(() => '')
    }
  }, [menuId, clinicId])

  const handleFormError = () => {
    if (showGenderSelection && !canProceedWithGender) {
      setGenderError(true)
    }
    const err = form.formState.errors
    const firstErrName = Object.keys(err)[0] as keyof UserRequestInterface
    if (firstErrName) setFocus(firstErrName)
  }

  const handleFormSubmit = async (data: UserRequestInterface) => {
    const effectiveGender = resolveEffectiveGender({
      ...genderSources,
      formGenderId: data.gender_id,
    })

    if (showGenderSelection && !effectiveGender) {
      setGenderError(true)
      return
    }

    form.clearErrors('gender_id')

    if (isMenuGenderIncompatible(menuTargetGender, effectiveGender)) {
      form.setError('gender_id', {
        type: 'manual',
        message: 'このメニューは選択した性別ではご利用できません。',
      })
      return
    }

    // 3. Pre-compute destination before registration so the register thunk can
    // navigate immediately after setToken() — before getInfo() fires — keeping
    // RestrictLayout's (accessToken && isRegisterRoute) guard from seeing an
    // authenticated session on /register and redirecting to /other-menu (Case 4 fix).
    // [SCOPE] searchParams omitted for the OTHER_MENU branch — registration params
    // (uuid, clinicId, from, telemedicine…) have no meaning on /my-page/other-menu.
    // The Questions branch keeps searchParams so the questions page can read menuId.
    const nextStepPath = menuId
      ? AUTOMATIC_PLAN_PATH.QUESTIONS.replace(':id', menuId)
      : PATH_MY_PAGE.OTHER_MENU
    const navigationSearch = menuId ? `?${searchParams.toString()}` : ''
    const nextUrl = `${nextStepPath}${navigationSearch}`

    try {
      // 4. Synchronization Phase: Ensure the user account is up-to-date
      if (userInfo) {
        await dispatch(
          updateUserInfo({
            ...data,
            gender_id: effectiveGender,
          }),
        ).unwrap()
      } else {
        const registrationSuccess = await onSubmitRegister(
          {
            ...data,
            gender_id: effectiveGender,
          },
          nextUrl, // register thunk calls history.push(nextUrl) before getInfo()
          '',
        )
        if (!registrationSuccess) return
      }
    } catch {
      // Thunk middleware centralises error notifications (toast/alert).
      // Bail out here to prevent navigation on a failed register/update.
      return
    }

    // 5. Progression Phase: Navigate for the updateUserInfo branch (existing users).
    // For new users the register thunk already navigated via history.push(nextUrl),
    // so this call is a harmless no-op duplicate that keeps both branches consistent.
    navigate(nextUrl, { replace: true })

    // 5. Initialization Phase: Create treatment and refresh state after navigation.
    // User is already on the destination route, so RestrictLayout is no longer
    // watching /register — these async calls complete safely in the background.
    // A separate try-catch is required here: navigate() has already fired, so a
    // failure must NOT silently swallow errors — the user is on the Questions page
    // without a treatment. Thunk middleware will show a notification; the user can
    // reload or retry from that page (SF-1 fix).
    if (menuId) {
      try {
        await dispatch(
          createUserTreatment({
            data: { menu_id: Number(menuId), plan_id: planId },
            form: form as any,
          }),
        ).unwrap()

        await dispatch(
          getInfo({
            with: 'userTreatments,userTreatments.reservations,userTreatments.reservations.answers',
          }),
        ).unwrap()
      } catch {
        // Thunk middleware already displayed the error notification.
        // User is on the Questions page — no further navigation needed.
      }
    }
  }

  return (
    <React.Fragment>
      <StyleForm as={Component}>
        <Row className="justify-content-between">
          <Col xs={11}>
            <InputLabel className="font-weight-bold">姓</InputLabel>
            <InputField<UserRequestInterface>
              controller={{
                control,
                name: 'family_name',
              }}
              inputProps={{
                placeholder: '入力してください',
                disabled,
              }}
            />
          </Col>
          <Col xs={11} className="ml-10">
            <Row className="justify-content-between">
              <InputLabel className="font-weight-bold">名</InputLabel>
              <LabelRequire>
                <span>必須</span>
              </LabelRequire>
            </Row>
            <InputField<UserRequestInterface>
              controller={{
                control,
                name: 'given_name',
              }}
              inputProps={{
                placeholder: '入力してください',
                disabled,
              }}
            />
          </Col>
        </Row>
        <Row className="justify-content-between">
          <Col xs={11}>
            <InputLabel className="font-weight-bold">姓(カナ)</InputLabel>
            <InputField<UserRequestInterface>
              controller={{
                control,
                name: 'family_name_kana',
              }}
              inputProps={{
                placeholder: '入力してください',
                disabled,
              }}
            />
          </Col>
          <Col xs={11} className="ml-10">
            <Row className="justify-content-between">
              <InputLabel className="font-weight-bold">名(カナ)</InputLabel>
              <LabelRequire>
                <span>必須</span>
              </LabelRequire>
            </Row>
            <InputField<UserRequestInterface>
              controller={{
                control,
                name: 'given_name_kana',
              }}
              inputProps={{
                placeholder: '入力してください',
                disabled,
              }}
            />
          </Col>
        </Row>

        <Col xs={24} className="mt-20">
          <Row className="justify-content-between">
            <InputLabel className="font-weight-bold">メールアドレス</InputLabel>
            <LabelRequire>
              <span>必須</span>
            </LabelRequire>
          </Row>
          <InputField<UserRequestInterface>
            controller={{
              control,
              name: 'email',
            }}
            inputProps={{
              placeholder: '入力してください',
              type: 'email',
              disabled,
              onChange: () => setIsEmailVerified(false),
              onBlur: (e: React.FocusEvent<HTMLInputElement>) =>
                handleBlur(e.target.value),
            }}
          />
        </Col>

        {showGenderSelection && (
          <Row className="mt-20">
            <Col xs={24}>
              <Row className="justify-content-between">
                <InputLabel className="font-weight-bold">
                  性別（どちらかを選択してください）
                </InputLabel>
                <LabelRequire>
                  <span>必須</span>
                </LabelRequire>
              </Row>
              <div
                style={
                  disabled ? { pointerEvents: 'none', opacity: 0.5 } : undefined
                }
              >
                <GenderSelection
                  value={genderDisplayValue}
                  onChange={(val) => {
                    setUserSelectedGender(val)
                    setStoredSelectedGender(val)
                    setGenderError(false)
                    form.setValue('gender_id', val)
                    form.clearErrors('gender_id')
                  }}
                />
              </div>
              {genderError && (
                <Typography.Text
                  className="ant-form-item-explain ant-form-item-explain-error pt-1"
                  type="danger"
                  style={{
                    overflow: 'visible',
                    whiteSpace: 'break-spaces',
                    display: 'block',
                  }}
                >
                  選択してください。
                </Typography.Text>
              )}

              {form.formState.errors.gender_id && (
                <Typography.Text
                  className="ant-form-item-explain ant-form-item-explain-error pt-1"
                  type="danger"
                >
                  {form.formState.errors.gender_id.message}
                </Typography.Text>
              )}
            </Col>
          </Row>
        )}

        <Col xs={24} className="mt-15 d-flex">
          <label className="agree-policy-label">
            <input
              type="checkbox"
              checked={isAgreePolicy}
              disabled={disabled}
              onChange={(e) => setIsAgreePolicy(e.target.checked)}
            />
            <span>
              <a
                href="https://bioportplus.jp/terms"
                target="_blank"
                rel="noopener noreferrer"
              >
                利用規約
              </a>
              および、
              <a
                href="https://bioportplus.jp/policy"
                target="_blank"
                rel="noopener noreferrer"
              >
                プライバシーポリシー
              </a>
              に同意する
            </span>
          </label>
        </Col>

        <ButtonCustom
          className="btn-default btn-chestnut-rose br-60 mt-30"
          loading={isSubmitting}
          htmlType="button"
          onClick={handleSubmit(handleFormSubmit, handleFormError)}
          name="次へ進む"
          showIconRight
          disabled={
            disabled ||
            !isAgreePolicy ||
            !canProceedWithGender ||
            (!userInfo && !isEmailVerified)
          }
        />
      </StyleForm>

      <DuplicateAccountModal
        open={isDuplicateModalOpen}
        onClose={() => setIsDuplicateModalOpen(false)}
      />
    </React.Fragment>
  )
}

const StyleForm = styled(Form)`
  .agree-policy-label {
    display: flex;
    align-items: center;
    gap: 8px;
    color: var(--color-black, #333);
    font-size: 14px;

    a {
      color: #1890ff;
      text-decoration: underline;
    }
  }

  .input-birthday {
    .aga-input {
      font-size: 16px;
    }
  }

  .picker-group {
    position: fixed;
    width: 100%;
    bottom: 0;
    left: 0;
    height: 100vh;
    background: #000000a8;
    z-index: 100;

    &__item {
      position: absolute;
      width: 100%;
      background-color: #fff;
      bottom: 0;
      left: 0;
      font-size: 16px;
      border-radius: 10px 10px 0 0;

      .birth-action {
        background-color: #ffffff;
        padding: 15px;
        border-bottom: 1px solid #998800;
        border-radius: 10px 10px 0 0;
        justify-content: space-between;

        .btn-warning {
          color: var(--color-dusty-gray);
          background-color: var(--color-white);
          cursor: pointer;
        }

        .btn-gray {
          color: #999999;
          cursor: pointer;
        }
      }
    }
  }

  .picker-date {
    background-color: #f1efe7;
    border: 1px solid #cfc392;
    padding: 10px;
  }

  .picker-inner {
    -webkit-mask-box-image-source: none !important;
  }

  .picker-container .picker-column .picker-item.picker-item-selected {
    font-weight: bold;
  }
`

export default UserInfoForm
